import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PLATFORM_IMAGES } from "@/constants/platformImages";

export default function ShopifyIntegration() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/settings', { replace: true })}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Settings
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-background border border-border/30">
              <img src={PLATFORM_IMAGES.shopify} alt="Shopify" className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-foreground">Shopify Blog Integration</h1>
              <p className="text-muted-foreground mt-2">
                Connect your Shopify store to publish SEO-optimized articles directly to your Shopify blog
              </p>
            </div>
          </div>
        </div>

        {/* Prerequisites */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Prerequisites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              To enable integration with Shopify, you need:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-muted-foreground">
              <li>A Shopify store with admin access</li>
            </ul>
          </CardContent>
        </Card>

        {/* Step 1 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 1: Locate Your Shopify Credentials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Before connecting, follow these steps to get the required credentials:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">Log in to your Shopify Admin Panel</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Go to Apps → Apps and sales channels</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">Click on "Develop apps" on top, and "Allow custom app development"</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">4</Badge>
                <div>
                  <p className="font-medium">Create an app, and add any name (e.g., SEORO)</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">5</Badge>
                <div>
                  <p className="font-medium">Navigate to the API credentials tab, and click "Configure Admin API scopes"</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">6</Badge>
                <div>
                  <p className="font-medium">Select these 2 permissions:</p>
                  <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                    <li className="text-muted-foreground">Write_content</li>
                    <li className="text-muted-foreground">Read_content</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">7</Badge>
                <div>
                  <p className="font-medium">Validate and click "Create app". It will show your Admin API access token</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">8</Badge>
                <div>
                  <p className="font-medium">Select "Reveal token once" and copy the Admin API access token (starts with <code className="bg-muted px-1 rounded">shpat_</code>). Save it somewhere safe.</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    <strong>Note:</strong> This token is only shown once. Make sure to copy it somewhere safe.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg mt-4">
              <p className="font-medium mb-2">Your Shopify domain</p>
              <p className="text-sm text-muted-foreground">
                Your Shopify domain is displayed in the top-left corner of your admin panel.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Step 2 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 2: Connect SEORO to Shopify</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">Log in to SEORO and navigate to Settings → Integrations</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Under Publishing Platforms, click the Connect button next to Shopify</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">In the popup:</p>
                  <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                    <li className="text-muted-foreground">Paste the Admin API key you copied earlier</li>
                    <li className="text-muted-foreground">Enter your Shopify store domain</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">4</Badge>
                <div>
                  <p className="font-medium">Click Verify</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 3: Choose the Blog to Publish Into</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">After verification, you will be prompted to select a Shopify blog from a dropdown list</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Choose the blog where you want your SEORO articles to appear</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">Click Connect to finalize</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 4 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Step 4: You Are Connected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You'll be redirected back to SEORO with a success message confirming your Shopify integration.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              If you face any issues, contact support using the sidebar chat within the app.
            </p>
          </CardContent>
        </Card>

        {/* Publishing Workflow */}
        <Card>
          <CardHeader>
            <CardTitle>Publishing Workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              • Articles marked as "Published" in SEORO will be immediately published to your Shopify blog.
            </p>
            <p className="text-muted-foreground">
              • Articles marked as "Scheduled" will be automatically published at the scheduled time.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

